import React, {useCallback, useMemo} from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ClearIcon from "@mui/icons-material/Clear";
import {Box, IconButton, InputAdornment, MenuItem, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {appSelector} from "../../state/app/appSelectors";
import {localizeI18nObj} from "../../utils/i18n";

/**
 * Builds a hierarchical tree structure from a flat list of items.
 * @param {Array} items - Flat list of items
 * @param {string} idKey - Property name for the unique identifier
 * @param {string} labelKey - Property name for the display label
 * @param {string} parentKey - Property name for the parent reference
 */
function buildHierarchy(items, idKey, labelKey, parentKey) {
  const nodes = new Map();
  const roots = new Set();

  for (const item of items) {
    if (!item || item[idKey] == null) continue;

    const id = item[idKey];
    const label = item[labelKey];
    const parent = item[parentKey];

    // create the node if it doesn't exist
    if (!nodes.has(id)) {
      nodes.set(id, {id, label, children: []});
    } else {
      // in case it was created as a placeholder
      nodes.get(id).label = label;
    }

    if (parent == null) {
      roots.add(id);
    } else {
      // create the parent if it doesn't exist yet
      if (!nodes.has(parent)) {
        nodes.set(parent, {id: parent, label: null, children: []});
      }

      nodes.get(parent).children.push(nodes.get(id));
      roots.delete(id);
    }
  }

  return Array.from(roots).map(id => nodes.get(id));
}

/**
 * Builds the path (array of node ids) from the root to the target node.
 */
const buildPathToValue = (nodes, target, path = []) => {
  for (const node of nodes) {
    if (node.id === target) {
      return [...path, node.id];
    }
    const sub = buildPathToValue(node.children, target, [...path, node.id]);
    if (sub) {
      return sub;
    }
  }
  return null;
};

/**
 * Internal recursive component that renders each level of the hierarchy.
 */
const HierarchicalSelectLevel = React.memo(
  ({
    className,
    sx,
    nodes,
    level = 0,
    path = [],
    onPathChange,
    label = "",
    levelLabel,
    disabled,
    required,
    clearable,
    ...props
  }) => {
    const {t} = useTranslation();
    const {language, languages} = useSelector(appSelector);

    const handleChange = useCallback(
      e => {
        const value = e.target.value;
        if (value === "") {
          // when deselecting, return the parent path (up to current level)
          onPathChange(path.slice(0, level));
        } else {
          onPathChange([...path.slice(0, level), value]);
        }
      },
      [onPathChange, path, level]
    );

    const handleClear = useCallback(() => onPathChange(null), [onPathChange]);

    const selectedId = path[level] || "";
    const selectedNode = nodes.find(n => n.id === selectedId);

    return (
      <div className={className}>
        <Box sx={{display: "flex", alignItems: "center"}}>
          {level !== 0 && (
            <Box sx={{paddingLeft: `${24 * (level - 1)}px`, paddingRight: "4px"}}>
              <ArrowBackIosNewIcon sx={{transform: "rotate(-45deg)"}} />
            </Box>
          )}
          <TextField
            select
            fullWidth
            variant="outlined"
            value={selectedId}
            label={
              level === 0
                ? levelLabel
                  ? `${label} ${levelLabel} ${level + 1}`
                  : label
                : levelLabel
                  ? `${levelLabel} ${level + 1}`
                  : ""
            }
            onChange={handleChange}
            disabled={disabled}
            required={required}
            slotProps={{
              input: {
                endAdornment: clearable && selectedId && (
                  <InputAdornment sx={{marginRight: "8px"}} position="end">
                    <IconButton onClick={handleClear}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
            {...props}
            sx={[
              {
                margin: "8px 0"
              },
              sx
            ]}
          >
            <MenuItem value={""}>
              <em>{t("components.hierarchicalSelect.menuItem.none.title")}</em>
            </MenuItem>
            {nodes.map(node => (
              <MenuItem key={node.id} value={node.id}>
                {localizeI18nObj(node.label, language, languages) || node.id}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {selectedNode && selectedNode.children.length > 0 && (
          <HierarchicalSelectLevel
            nodes={selectedNode.children}
            level={level + 1}
            path={path}
            onPathChange={onPathChange}
            levelLabel={levelLabel}
            disabled={disabled}
            required={required}
            {...props}
          />
        )}
      </div>
    );
  }
);

/**
 * HierarchicalSelect component that accepts a flat list of items and builds
 * the hierarchy internally, allowing hierarchical selection.
 *
 * @param {Array} items - Flat list of items
 * @param {string} value - Currently selected value (leaf node's id)
 * @param {function} onChange - Callback when selection changes, receives the selected id or ""
 * @param {string} idKey - Property name for the unique identifier (default: "id")
 * @param {string} labelKey - Property name for the display label (default: "label")
 * @param {string} parentKey - Property name for the parent reference (default: "parent")
 * @param {string} label - Label for the first level
 * @param {string} levelLabel - Label prefix for each level (e.g., "Level")
 * @param {boolean} disabled - Disable the select
 * @param {boolean} required - Mark as required
 * @param {boolean} clearable - Show clear button
 */
const HierarchicalSelect = ({
  items = [],
  value,
  onChange,
  idKey = "id",
  labelKey = "label",
  parentKey = "parent",
  className,
  sx,
  label,
  levelLabel,
  disabled,
  required,
  clearable,
  ...props
}) => {
  const nodes = useMemo(() => buildHierarchy(items, idKey, labelKey, parentKey), [items, idKey, labelKey, parentKey]);
  const path = useMemo(() => (value ? (buildPathToValue(nodes, value) ?? []) : []), [nodes, value]);

  const handlePathChange = useCallback(
    newPath => {
      if (newPath && newPath.length > 0) {
        onChange(newPath[newPath.length - 1]);
      } else {
        onChange("");
      }
    },
    [onChange]
  );

  return (
    <HierarchicalSelectLevel
      className={className}
      sx={sx}
      nodes={nodes}
      path={path}
      onPathChange={handlePathChange}
      label={label}
      levelLabel={levelLabel}
      disabled={disabled}
      required={required}
      clearable={clearable}
      {...props}
    />
  );
};

export default HierarchicalSelect;
